"use client";

import { Check } from "lucide-react";
import { useEffect } from "react";

interface OptionCardProps {
  label: string;
  emoji?: string;
  description?: string;
  shortcut?: string;
  selected?: boolean;
  onClick: () => void;
}

export function OptionCard({
  label,
  emoji,
  description,
  shortcut,
  selected = false,
  onClick,
}: OptionCardProps) {
  useEffect(() => {
    if (!shortcut) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (event.key.toUpperCase() === shortcut.toUpperCase()) {
        event.preventDefault();
        onClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClick, shortcut]);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`group relative min-h-[76px] w-full rounded-xl border p-4 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14] sm:p-5 ${
        selected
          ? "border-[#FD954F] bg-[#FD954F]/10"
          : "border-white/10 bg-[#15161B] hover:border-[#FD954F]/50 hover:bg-[#1B1C22]"
      }`}
    >
      <span
        className={`absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg border font-mono text-sm font-bold transition-colors ${
          selected
            ? "border-[#FD954F]/50 bg-[#FD954F] text-[#1C1C1E]"
            : "border-white/10 bg-[#1B1C22] text-gray-500 group-hover:border-[#FD954F]/50 group-hover:text-[#FD954F]"
        }`}
        aria-hidden="true"
      >
        {selected ? <Check className="h-5 w-5" /> : shortcut}
      </span>

      <span className="flex items-start gap-4 pr-10">
        {emoji && (
          <span className="flex-shrink-0 text-3xl" aria-hidden="true">
            {emoji}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="mb-1 block text-lg font-bold text-white">{label}</span>
          {description && <span className="block text-sm text-gray-400">{description}</span>}
        </span>
      </span>
    </button>
  );
}
